import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons'
import {
  FormLabel,
  GridItem,
  Heading,
  SimpleGrid,
  FormControl,
  Input,
  Button,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  InputGroup,
  InputRightElement,
  IconButton,
  Box,
  FormErrorMessage,
  useBoolean,
  useToast,
} from '@chakra-ui/react'
import { ChangeEvent, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useLocation } from 'react-router-dom'
import Breadcrumbs from '../../components/breadcrumbs/breadcrumbs'
import PermissionDenied from '../../components/permissionDenied/permissionDenied'
import ReportingTab from '../../components/reporting/reporting'
import { USER_AVATAR } from '../../constants'
import {
  useAccountUpdateMutation,
  useChangePasswordMutation,
  useSendResetPasswordEmailMutation,
  useUserGetOneQuery,
} from '../../graphql/generated/graphql'
import { useAuth } from '../../hooks/use-auth'
import { isCurrentUserStaff } from '../../utils/helpers/basicFunc'
import {
  PasswordChecklist,
  PasswordConditions,
  passwordRegex,
  PASSWORD_CONDITIONS,
} from '../setResetPassword'

type UserEditFormValues = {
  firstName: string
  lastName: string
  picture: string
}

type changePasswordFormValues = {
  currentPassword: string
  password: string
  confirmPassword: string
}

const UserSettings = () => {
  const auth = useAuth(),
    location = useLocation(),
    slug = location.pathname.split('/')[2],
    toast = useToast()

  const isStaffLoggedInAsUser =
    auth.user.id === 'staff' && auth.user.slug !== slug

  const [passwordConditions, setPasswordConditions] =
    useState<PasswordConditions>({
      ...PASSWORD_CONDITIONS,
    })
  const [currentPasswordView, setCurrentPasswordView] = useBoolean()
  const [confirmPasswordView, setConfirmPasswordView] = useBoolean()
  const [newPasswordView, setNewPasswordView] = useBoolean()

  const toastFunc = (title: string, status: 'success' | 'error') => {
    toast({
      title,
      status,
      position: 'top',
      duration: 3000,
      isClosable: true,
    })
  }

  const { handleSubmit, register } = useForm<UserEditFormValues>()
  const {
    handleSubmit: handlePasswordSubmit,
    register: registerPasswordForm,
    watch,
    formState: { errors },
  } = useForm<changePasswordFormValues>()
  const password = watch('password')

  const { data: userFromQuery } = useUserGetOneQuery({
    variables: { where: { slug } },
  })
  const [userUpdate, { loading: userUpdateLoading }] =
    useAccountUpdateMutation()
  const [changePassword] = useChangePasswordMutation()
  const [resetPassword, { loading: forgotPasswordLoading }] =
    useSendResetPasswordEmailMutation()

  const userDetails = userFromQuery?.userGetOne

  if (!isCurrentUserStaff() && auth.user.slug !== slug) {
    return <PermissionDenied />
  }
  const breadcrumbLinks = [
    { text: 'Home', route: '/' },
    { text: 'Account', route: '/usersettings' },
  ]

  const onSubmit = (formValues: UserEditFormValues) => {
    if (userDetails?.id) {
      const data = {
        id: userDetails.id,
        firstName: formValues.firstName.length
          ? formValues.firstName
          : userDetails.firstName,
        lastName: formValues.lastName.length
          ? formValues.lastName
          : userDetails.lastName,
        picture: formValues.picture.length
          ? formValues.picture
          : userDetails.picture,
      }
      if (formValues.picture !== userDetails.picture) {
        localStorage.setItem(USER_AVATAR, formValues.picture)
      }
      userUpdate({ variables: { data } })
    }
  }

  const onPasswordSubmit = (passwordValues: changePasswordFormValues) => {
    changePassword({
      variables: {
        oldPassword: passwordValues.currentPassword,
        newPassword: passwordValues.password,
      },
      onCompleted: (response) => {
        toast({
          title: response.changePassword.message,
          status: 'success',
          position: 'top',
          duration: 3000,
          isClosable: true,
        })
      },
    })
  }

  const onPasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { target } = e,
      { value } = target
    const newConditions: PasswordConditions = {
      moreThanTwelveCharacters: value.length < 12 || value.length > 22,
      upperCase: !/[A-Z]/.test(value) || value.length > 22,
      lowerCase: !/[a-z]/.test(value) || value.length > 22,
      number: !/\d/.test(value) || value.length > 22,
      specialCharacters: !/[#?!@$%^&*-]/.test(value) || value.length > 22,
      moreThanTwentyTwoCharacter: value.length <= 22,
    }

    setPasswordConditions(newConditions)
  }

  const onForgotPassword = () => {
    resetPassword({
      variables: {
        resetType: 'reset',
        email: userDetails?.email || '',
      },
      onCompleted: (response) => {
        toastFunc(response.sendResetPasswordEmail.message, 'success')
      },
      onError: (err) => {
        toastFunc(err.message, 'error')
      },
    })
  }

  return (
    <SimpleGrid columns={12} padding={{ base: '0 15px', md: 0 }}>
      <GridItem
        colStart={{ base: 1, sm: 1, md: 2, lg: 3 }}
        colSpan={{ base: 12, sm: 12, md: 10, lg: 8 }}
      >
        <Breadcrumbs links={breadcrumbLinks} />
        <Tabs isFitted>
          <TabList>
            <Tab>Basic settings</Tab>
            {isStaffLoggedInAsUser ? (
              <Tab>Reset Password</Tab>
            ) : (
              <Tab>Change Password</Tab>
            )}

            <Tab>Reporting</Tab>
          </TabList>

          <TabPanels>
            <TabPanel>
              <Heading mb={5}>User Settings</Heading>
              <form onSubmit={handleSubmit(onSubmit)}>
                <FormControl>
                  <FormLabel>First Name</FormLabel>
                  <Input
                    defaultValue={userDetails?.firstName ?? ''}
                    {...register('firstName')}
                  />
                </FormControl>
                <FormControl mt={4}>
                  <FormLabel>Last Name</FormLabel>
                  <Input
                    defaultValue={userDetails?.lastName ?? ''}
                    {...register('lastName')}
                  />
                </FormControl>
                <FormControl mt={4}>
                  <FormLabel>Picture</FormLabel>
                  <Input
                    defaultValue={userDetails?.picture ?? ''}
                    {...register('picture')}
                  />
                </FormControl>
                <Button
                  type="submit"
                  colorScheme={'purple'}
                  mt={5}
                  isLoading={userUpdateLoading}
                >
                  Save
                </Button>
              </form>
            </TabPanel>
            <TabPanel>
              {isStaffLoggedInAsUser ? (
                <>
                  <FormControl id="email">
                    <FormLabel>Email</FormLabel>
                    <Input
                      type="email"
                      value={userDetails?.email || ''}
                      disabled
                    />
                  </FormControl>
                  <Button
                    mt={4}
                    colorScheme={'purple'}
                    onClick={() => onForgotPassword()}
                    isLoading={forgotPasswordLoading}
                  >
                    Send
                  </Button>
                </>
              ) : (
                <form onSubmit={handlePasswordSubmit(onPasswordSubmit)}>
                  <FormControl
                    id="currentPassword"
                    isInvalid={!!errors.currentPassword}
                    mt={4}
                  >
                    <FormLabel>Current Password</FormLabel>
                    <InputGroup>
                      <Input
                        type={currentPasswordView ? 'text' : 'password'}
                        placeholder=" "
                        {...registerPasswordForm('currentPassword', {
                          required: 'Please fill in the field',
                        })}
                      />

                      <InputRightElement>
                        <IconButton
                          size="sm"
                          onClick={setCurrentPasswordView.toggle}
                          aria-label={'hide-pass'}
                          icon={
                            currentPasswordView ? <ViewIcon /> : <ViewOffIcon />
                          }
                        />
                      </InputRightElement>
                    </InputGroup>
                    {/* <Box minH={8} mt={2}></Box> */}
                  </FormControl>
                  <FormControl
                    id="password"
                    isInvalid={!!errors.password}
                    mt={4}
                  >
                    <FormLabel>Set Password</FormLabel>
                    <InputGroup>
                      <Input
                        type={newPasswordView ? 'text' : 'password'}
                        placeholder=" "
                        {...registerPasswordForm('password', {
                          onChange: (e) => onPasswordChange(e),
                          required: 'Please fill in the field',
                          pattern: {
                            value: passwordRegex,
                            message: '',
                          },
                        })}
                      />

                      <InputRightElement>
                        <IconButton
                          size="sm"
                          onClick={setNewPasswordView.toggle}
                          aria-label={'hide-password'}
                          icon={
                            newPasswordView ? <ViewIcon /> : <ViewOffIcon />
                          }
                        />
                      </InputRightElement>
                    </InputGroup>
                    <Box>
                      {Object.values(passwordConditions).some(
                        (value) => !value,
                      ) && (
                        <PasswordChecklist
                          passwordConditions={passwordConditions}
                        />
                      )}
                    </Box>
                  </FormControl>

                  <FormControl
                    id="confirmPassword"
                    isInvalid={!!errors.confirmPassword}
                    mt={4}
                  >
                    <FormLabel>Confirm Password</FormLabel>
                    <InputGroup>
                      <Input
                        type={confirmPasswordView ? 'text' : 'password'}
                        placeholder=" "
                        {...registerPasswordForm('confirmPassword', {
                          required: 'Please fill in the field',
                          validate: (value) =>
                            value === password || 'Passwords do not match', // Custom validation
                        })}
                      />

                      <InputRightElement>
                        <IconButton
                          size="sm"
                          onClick={setConfirmPasswordView.toggle}
                          aria-label={'hide-pass'}
                          icon={
                            confirmPasswordView ? <ViewIcon /> : <ViewOffIcon />
                          }
                        />
                      </InputRightElement>
                    </InputGroup>
                    {errors.confirmPassword ? (
                      <FormErrorMessage minH={8}>
                        {errors.confirmPassword.message as string}
                      </FormErrorMessage>
                    ) : (
                      <></>
                    )}
                  </FormControl>
                  <Button type={'submit'} colorScheme={'purple'} mt={4}>
                    Save
                  </Button>
                </form>
              )}
            </TabPanel>
            <TabPanel>
              <ReportingTab userDetails={userFromQuery?.userGetOne} />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </GridItem>
    </SimpleGrid>
  )
}

export default UserSettings
